import {
  HOME_HREF, matchesRoute, normalizeSearch, searchNavigation,
} from '@/utils/navigationMenu';
import { readClaims } from '@/utils/tokenClaims';

export const FAVORITES_LIMIT = 4;
const RECENTS_STORED = 20;
const RECENTS_SHOWN = 10;
const SEARCHES_STORED = 5;

// ── Owner ──

const isId = (value) => (typeof value === 'number' || typeof value === 'string') && String(value) !== '';

const NO_PROMOTER = 'none';

export function navigationOwner({ userId, token }) {
  const promoterId = readClaims(token).promoter_id;

  if (!isId(userId)) return null;
  if (promoterId === null) return { userId, promoterId: NO_PROMOTER };

  return isId(promoterId) ? { userId, promoterId } : null;
}

const storageKey = (kind, { userId, promoterId }) => `navigation_${kind}:${userId}:${promoterId}`;

function readList(kind, owner) {
  if (!owner) return null;

  try {
    const list = JSON.parse(localStorage.getItem(storageKey(kind, owner)));

    return Array.isArray(list) ? list : null;
  } catch {
    return null;
  }
}

function writeList(kind, owner, list) {
  if (!owner) return false;

  try {
    localStorage.setItem(storageKey(kind, owner), JSON.stringify(list));
    return true;
  } catch {
    return false;
  }
}

// ── Favorites ──

const isIdentity = (entry) => entry !== null
  && typeof entry === 'object'
  && typeof entry.text === 'string'
  && typeof entry.href === 'string'
  && (entry.group === null || typeof entry.group === 'string');

const identityOf = (item) => ({ text: item.text, group: item['menu-item'] ?? null, href: item.href });

const sameIdentity = (a, b) => a.href === b.href;

const liveItem = (items, entry) => items.find((item) => sameIdentity(identityOf(item), entry));

export function readFavorites(owner) {
  const list = readList('favorites', owner);

  return list && list.every(isIdentity) ? list : null;
}

export const writeFavorites = (owner, list) => writeList('favorites', owner, list);

const defaultFavorites = (items, landing) => items
  .filter((item) => item.href === landing && item.href !== HOME_HREF)
  .slice(0, 1)
  .map(identityOf);

export function resolveFavorites({ stored, items, landing }) {
  return (stored ?? defaultFavorites(items, landing))
    .map((entry) => liveItem(items, entry))
    .filter(Boolean)
    .slice(0, FAVORITES_LIMIT);
}

export const isFavorite = (favorites, item) => favorites
  .some((favorite) => sameIdentity(identityOf(favorite), identityOf(item)));

export function toggleFavorite({
  stored, items, landing, item,
}) {
  const current = stored ?? defaultFavorites(items, landing);
  const target = identityOf(item);

  if (current.some((entry) => sameIdentity(entry, target))) {
    return { list: current.filter((entry) => !sameIdentity(entry, target)), blocked: false };
  }

  if (resolveFavorites({ stored: current, items, landing }).length >= FAVORITES_LIMIT) {
    return { list: current, blocked: true };
  }

  const list = [...current, target];

  if (list.length <= FAVORITES_LIMIT) return { list, blocked: false };

  return { list: list.filter((entry) => liveItem(items, entry)), blocked: false };
}

// ── Recents ──

const isVisit = (entry) => entry !== null
  && typeof entry === 'object'
  && typeof entry.path === 'string'
  && typeof entry.title === 'string'
  && Number.isFinite(entry.visitedAt);

const isEmpty = (record) => !record || Object.keys(record).length === 0;

export function recentVisit({
  route, title, framed, now,
}) {
  if (!framed || !isEmpty(route.params) || !isEmpty(route.query)) return null;

  return { path: route.path, title: typeof title === 'string' ? title : '', visitedAt: now };
}

export function readRecents(owner) {
  return (readList('recents', owner) ?? []).filter(isVisit);
}

export const writeRecents = (owner, list) => writeList('recents', owner, list);

export const addRecent = (list, visit) => [visit, ...list.filter(({ path }) => path !== visit.path)]
  .slice(0, RECENTS_STORED);

export const recordRecent = (owner, visit) => writeRecents(
  owner,
  addRecent(readRecents(owner), visit),
);

function recentItem(items, { path, title }) {
  const item = items.find((candidate) => matchesRoute(candidate, path));

  if (item) return item;

  return title ? { icon: 'fas fa-history', href: path, text: title } : null;
}

export function resolveRecents({
  stored, items, favorites, currentPath = null,
}) {
  const seen = new Set();
  const isCurrent = (item) => typeof currentPath === 'string' && matchesRoute(item, currentPath);

  return stored
    .map((visit) => ({ visit, item: recentItem(items, visit) }))
    .filter(({ item }) => {
      if (!item || seen.has(item.href)) return false;
      if (isFavorite(favorites, item) || isCurrent(item)) return false;

      seen.add(item.href);
      return true;
    })
    .slice(0, RECENTS_SHOWN)
    .map(({ visit, item }) => ({ ...item, visitedAt: visit.visitedAt }));
}

export function groupRecentsByDay(entries, now) {
  const midnight = new Date(now);
  midnight.setHours(0, 0, 0, 0);
  const today = midnight.getTime();
  const yesterday = midnight.setDate(midnight.getDate() - 1);
  const label = ({ visitedAt }) => {
    if (visitedAt >= today) return 'Hoje';
    if (visitedAt >= yesterday) return 'Ontem';
    return 'Anteriores';
  };

  return ['Hoje', 'Ontem', 'Anteriores']
    .map((name) => ({ label: name, items: entries.filter((entry) => label(entry) === name) }))
    .filter(({ items }) => items.length);
}

// ── Searches ──

const searchTerm = (query) => (typeof query === 'string' ? query.trim().replace(/\s+/g, ' ') : '');

const sameSearch = (a, b) => normalizeSearch(a) === normalizeSearch(b);

const findsItems = (sections, term) => Boolean(searchNavigation(sections, term)?.length);

export const readSearches = (owner) => (readList('searches', owner) ?? [])
  .filter((term) => typeof term === 'string' && term !== '');

export const writeSearches = (owner, list) => writeList('searches', owner, list);

export const removeSearch = (list, term) => list.filter((entry) => !sameSearch(entry, term));

export function recordSearch(owner, query, sections) {
  const term = searchTerm(query);

  if (!owner || !findsItems(sections, term)) return false;

  const list = [term, ...removeSearch(readSearches(owner), term)];

  return writeSearches(owner, list.slice(0, SEARCHES_STORED));
}

export const resolveSearches = ({ stored, sections }) => stored
  .filter((term) => findsItems(sections, term))
  .slice(0, SEARCHES_STORED);
